import { useCallback, useEffect, useState } from 'react';
import { searchProducts, lookupProductByBarcode } from '../../../catalog/infra-api'
import { getProductById } from '../../../catalog/infra-api'
import { listVariantsForProduct } from '../../../catalog/infra-api'
import { useProductVariantsEnabled } from '../../../catalog/app-api';
import { isInventoriableProduct } from '../../../inventory/stock/public-api';
import type { ProductVariant } from '../../../catalog/domain-api';
import { isDatabaseUnavailableError } from '../../../shared/localDb';
import { PRODUCT_PAGE_SIZE } from '../../application/inventoryUtils';
import { useInventoryUiStore } from '../../store/index';
import type { Product } from '../../../catalog/domain-api';
import type { StockRow } from '../../../inventory/stock/public-api';
import { effectiveProductSearchQuery, PRODUCT_SEARCH_DEBOUNCE_MS } from '../../../shared/product-search/productSearchUx';

interface UseInventoryProductSearchParams {
  stocks: StockRow[];
  handleSelectProduct: (product: Product) => void;
}

export const useInventoryProductSearch = (params: UseInventoryProductSearchParams) => {
  const { stocks, handleSelectProduct } = params;

  const productQuery = useInventoryUiStore((s) => s.productQuery);
  const productQueryDebounced = useInventoryUiStore((s) => s.productQueryDebounced);
  const setProductQueryDebounced = useInventoryUiStore((s) => s.setProductQueryDebounced);
  const productResults = useInventoryUiStore((s) => s.productResults);
  const setProductResults = useInventoryUiStore((s) => s.setProductResults);
  const productPageOffset = useInventoryUiStore((s) => s.productPageOffset);
  const setProductPageOffset = useInventoryUiStore((s) => s.setProductPageOffset);
  const productHasMore = useInventoryUiStore((s) => s.productHasMore);
  const setProductHasMore = useInventoryUiStore((s) => s.setProductHasMore);
  const productLoadingMore = useInventoryUiStore((s) => s.productLoadingMore);
  const setProductLoadingMore = useInventoryUiStore((s) => s.setProductLoadingMore);
  const selectedProduct = useInventoryUiStore((s) => s.selectedProduct);
  const setSelectedProduct = useInventoryUiStore((s) => s.setSelectedProduct);
  const entryQuantity = useInventoryUiStore((s) => s.entryQuantity);
  const setEntryQuantity = useInventoryUiStore((s) => s.setEntryQuantity);
  const entryUnitCost = useInventoryUiStore((s) => s.entryUnitCost);
  const setEntryUnitCost = useInventoryUiStore((s) => s.setEntryUnitCost);
  const entryMinQuantity = useInventoryUiStore((s) => s.entryMinQuantity);
  const setEntryMinQuantity = useInventoryUiStore((s) => s.setEntryMinQuantity);
  const entryExpiry = useInventoryUiStore((s) => s.entryExpiry);
  const setEntryExpiry = useInventoryUiStore((s) => s.setEntryExpiry);
  const entryReason = useInventoryUiStore((s) => s.entryReason);
  const setEntryReason = useInventoryUiStore((s) => s.setEntryReason);
  const entrySupplierId = useInventoryUiStore((s) => s.entrySupplierId);
  const setEntrySupplierId = useInventoryUiStore((s) => s.setEntrySupplierId);
  const entryLotNumber = useInventoryUiStore((s) => s.entryLotNumber);
  const setEntryLotNumber = useInventoryUiStore((s) => s.setEntryLotNumber);
  const entryVariantId = useInventoryUiStore((s) => s.entryVariantId);
  const setEntryVariantId = useInventoryUiStore((s) => s.setEntryVariantId);
  const savingEntry = useInventoryUiStore((s) => s.savingEntry);
  const setSavingEntry = useInventoryUiStore((s) => s.setSavingEntry);
  const lastUnitCost = useInventoryUiStore((s) => s.lastUnitCost);
  const setLastUnitCost = useInventoryUiStore((s) => s.setLastUnitCost);
  const entryScanFeedback = useInventoryUiStore((s) => s.entryScanFeedback);
  const setEntryScanFeedback = useInventoryUiStore((s) => s.setEntryScanFeedback);
  const entrySuggestion = useInventoryUiStore((s) => s.entrySuggestion);
  const setEntrySuggestion = useInventoryUiStore((s) => s.setEntrySuggestion);
  const setProductQuery = useInventoryUiStore((s) => s.setProductQuery);
  const [availableVariants, setAvailableVariants] = useState<ProductVariant[]>([]);
  // #709: gatear la carga de variantes con la capability product_variants.
  const variantsEnabled = useProductVariantsEnabled();

  // Cargar variantes activas del producto seleccionado para el selector
  useEffect(() => {
    if (!selectedProduct || !variantsEnabled) {
      setAvailableVariants([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const variants = await listVariantsForProduct(selectedProduct.id);
        if (!cancelled) {
          setAvailableVariants(variants);
          // Reset variant selection if the current one is not in the new list
          if (entryVariantId && !variants.some((v) => v.id === entryVariantId)) {
            setEntryVariantId(null);
          }
        }
      } catch (error) {
        if (!isDatabaseUnavailableError(error)) {
          console.warn('[Inventory] No se pudieron cargar variantes', error);
        }
        if (!cancelled) setAvailableVariants([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedProduct, entryVariantId, setEntryVariantId, variantsEnabled]);

  useEffect(() => {
    const t = setTimeout(() => setProductQueryDebounced(productQuery.trim()), PRODUCT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [productQuery, setProductQueryDebounced]);

  const loadFirstPageProducts = useCallback(async () => {
    const search = effectiveProductSearchQuery(productQueryDebounced);
    if (!search) {
      setProductResults([]);
      setProductPageOffset(0);
      setProductHasMore(false);
      setProductLoadingMore(false);
      return;
    }
    setProductLoadingMore(true);
    setProductResults([]);
    setProductPageOffset(0);
    setProductHasMore(false);
    try {
      const rows = await searchProducts({
        search,
        limit: PRODUCT_PAGE_SIZE,
        offset: 0,
        inventoryPhysicalOnly: true,
      });
      setProductResults(rows);
      setProductPageOffset(rows.length);
      setProductHasMore(rows.length === PRODUCT_PAGE_SIZE);
    } finally {
      setProductLoadingMore(false);
    }
  }, [
    productQueryDebounced,
    setProductResults,
    setProductPageOffset,
    setProductHasMore,
    setProductLoadingMore,
  ]);

  useEffect(() => {
    let cancelled = false;
    loadFirstPageProducts().catch((error) => {
      if (!isDatabaseUnavailableError(error)) {
        console.warn('[Inventory] No se pudo cargar productos para alta de stock', error);
      }
      if (!cancelled) {
        setProductResults([]);
        setProductPageOffset(0);
        setProductHasMore(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loadFirstPageProducts, setProductResults, setProductPageOffset, setProductHasMore]);

  const loadMoreProducts = useCallback(async () => {
    if (productLoadingMore || !productHasMore) return;
    setProductLoadingMore(true);
    try {
      const search = effectiveProductSearchQuery(productQueryDebounced);
      if (!search) return;
      const rows = await searchProducts({
        search,
        limit: PRODUCT_PAGE_SIZE,
        offset: productPageOffset,
        inventoryPhysicalOnly: true,
      });
      setProductResults((current) => [...current, ...rows]);
      setProductPageOffset((current) => current + rows.length);
      setProductHasMore(rows.length === PRODUCT_PAGE_SIZE);
    } catch (error) {
      if (!isDatabaseUnavailableError(error)) {
        console.warn('[Inventory] No se pudo cargar más productos', error);
      }
      setProductHasMore(false);
    } finally {
      setProductLoadingMore(false);
    }
  }, [
    productHasMore,
    productLoadingMore,
    productPageOffset,
    productQueryDebounced,
    setProductResults,
    setProductPageOffset,
    setProductHasMore,
    setProductLoadingMore,
  ]);

  const wrappedHandleSelectProduct = useCallback(
    (product: Product) => {
      if (!isInventoriableProduct(product)) {
        return;
      }
      setSelectedProduct(product);
      setProductQuery(product.sku ?? '');
      const existingStock = stocks.find((row) => row.productId === product.id);
      setEntryMinQuantity(existingStock ? String(existingStock.minQuantity) : '0');
      handleSelectProduct(product);
    },
    [setSelectedProduct, setProductQuery, stocks, setEntryMinQuantity, handleSelectProduct]
  );

  const clearSelectedProduct = useCallback(() => {
    setSelectedProduct(null);
    setProductQuery('');
    setProductResults([]);
    setProductPageOffset(0);
    setProductHasMore(false);
    setEntryVariantId(null);
  }, [setSelectedProduct, setProductQuery, setProductResults, setProductPageOffset, setProductHasMore, setEntryVariantId]);

  return {
    productQuery,
    setProductQuery,
    productQueryDebounced,
    productResults,
    productPageOffset,
    productHasMore,
    productLoadingMore,
    selectedProduct,
    setSelectedProduct,
    entryQuantity,
    setEntryQuantity,
    entryUnitCost,
    setEntryUnitCost,
    entryMinQuantity,
    setEntryMinQuantity,
    entryExpiry,
    setEntryExpiry,
    entryReason,
    setEntryReason,
    entrySupplierId,
    setEntrySupplierId,
    entryLotNumber,
    setEntryLotNumber,
    entryVariantId,
    setEntryVariantId,
    availableVariants,
    savingEntry,
    setSavingEntry,
    lastUnitCost,
    setLastUnitCost,
    entryScanFeedback,
    setEntryScanFeedback,
    entrySuggestion,
    setEntrySuggestion,
    loadMoreProducts,
    handleSelectProduct: wrappedHandleSelectProduct,
    clearSelectedProduct,
  };
};
